import React, { useState } from 'react';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';

interface AdminInfo {
  admin_id: number;
  username: string;
  display_name: string;
  role: string;
}

interface CrawlerRequestProps {
  onQuery: (sport: string, date: string, partial: boolean, byDate?: boolean, endDate?: string, adminId?: number, notionAdminId?: number) => void;
  loading: boolean;
  aiProvider: string;
  setAiProvider: (provider: string) => void;
  initialData: {
    sports: Record<string, {
      name: string;
      leagues: Array<{value: string; label: string}>;
    }>;
    aiModels: Array<{value: string; label: string}>;
    defaultAIProvider: string;
  } | null;
  isAdministrator?: boolean;
  adminList?: AdminInfo[];
  currentAdminId?: number;
}

const CrawlerRequest: React.FC<CrawlerRequestProps> = ({
  onQuery, loading, aiProvider, setAiProvider, initialData,
  isAdministrator = false, adminList = [], currentAdminId
}) => {
  const [sport, setSport] = useState('all');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  // 현재 관리자 ID로 초기화하고, adminList가 로드된 후에도 유지
  const [selectedAdminId, setSelectedAdminId] = useState<number | undefined>(currentAdminId);
  // 노션 페이지 담당자 (별도 선택)
  const [notionAdminId, setNotionAdminId] = useState<number | undefined>(currentAdminId);

  // currentAdminId가 변경되면 selectedAdminId, notionAdminId도 업데이트
  React.useEffect(() => {
    if (currentAdminId && !selectedAdminId) {
      setSelectedAdminId(currentAdminId);
    }
    if (currentAdminId && !notionAdminId) {
      setNotionAdminId(currentAdminId);
    }
  }, [currentAdminId]);

  const sports = [
    { value: 'all', label: '전체' },
    ...(initialData
      ? Object.entries(initialData.sports).map(([value, data]) => ({ value, label: data.name }))
      : [
          { value: 'soccer', label: '축구' },
          { value: 'baseball', label: '야구' },
          { value: 'basketball', label: '농구' },
          { value: 'volleyball', label: '배구' },
          { value: 'hockey', label: '하키' }
        ])
  ];

  const handleQuery = () => {
    const formattedStartDate = startDate.replace(/-/g, '');
    const formattedEndDate = endDate.replace(/-/g, '');
    // endDate가 startDate와 같으면 단일 날짜
    const effectiveEndDate = formattedEndDate !== formattedStartDate ? formattedEndDate : undefined;
    const adminId = isAdministrator ? selectedAdminId : undefined;
    const effectiveNotionAdminId = isAdministrator ? notionAdminId : undefined;

    if (sport === 'all') {
      onQuery('', formattedStartDate, false, true, effectiveEndDate, adminId, effectiveNotionAdminId);
    } else {
      onQuery(sport, formattedStartDate, false, false, effectiveEndDate, adminId, effectiveNotionAdminId);
    }
  };

  const handlePartialQuery = () => {
    const formattedStartDate = startDate.replace(/-/g, '');
    const adminId = isAdministrator ? selectedAdminId : undefined;
    const effectiveNotionAdminId = isAdministrator ? notionAdminId : undefined;
    if (sport === 'all') {
      onQuery('', formattedStartDate, true, true, undefined, adminId, effectiveNotionAdminId);
    } else {
      onQuery(sport, formattedStartDate, true, false, undefined, adminId, effectiveNotionAdminId);
    }
  };

  // 날짜 범위 계산 (표시용)
  const getDateRangeInfo = () => {
    if (startDate === endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > 31) return { days: diffDays, warning: true };
    return { days: diffDays, warning: false };
  };

  const dateRangeInfo = getDateRangeInfo();

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>크롤링 요청</h3>

      <div style={styles.form}>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>시작일</label>
            <input
              type="date"
              style={styles.input}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                // 시작일이 종료일보다 늦으면 종료일도 같이 변경
                if (e.target.value > endDate) {
                  setEndDate(e.target.value);
                }
              }}
              disabled={loading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>종료일</label>
            <input
              type="date"
              style={styles.input}
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={loading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>종목</label>
            <select
              style={styles.select}
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              disabled={loading}
            >
              {sports.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              AI 모델
              <span style={styles.optionalText}> (선택)</span>
            </label>
            <select
              style={styles.select}
              value={aiProvider}
              onChange={(e) => setAiProvider(e.target.value)}
              disabled={loading}
            >
              <option value="">없음</option>
              {initialData?.aiModels.map(model => (
                <option key={model.value} value={model.value}>{model.label}</option>
              )) || (
                <>
                  <option value="gemini">Gemini</option>
                  <option value="claude">Claude</option>
                  <option value="openai">OpenAI</option>
                </>
              )}
            </select>
          </div>

          {isAdministrator && adminList.length > 0 && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>프롬프트 담당자</label>
                <select
                  style={styles.select}
                  value={selectedAdminId || ''}
                  onChange={(e) => setSelectedAdminId(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={loading}
                >
                  {adminList.map(admin => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>노션 페이지</label>
                <select
                  style={styles.select}
                  value={notionAdminId || ''}
                  onChange={(e) => setNotionAdminId(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={loading}
                >
                  {adminList.map(admin => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name} ({admin.role === 'administrator' ? '관리자' : '매니저'})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button
            style={{
              ...styles.button,
              ...styles.primaryButton,
              alignSelf: 'flex-end',
              opacity: dateRangeInfo?.warning ? 0.5 : 1
            }}
            onClick={handleQuery}
            disabled={loading || dateRangeInfo?.warning}
            title={
              dateRangeInfo?.warning
                ? "날짜 범위는 최대 31일까지만 가능함"
                : dateRangeInfo
                  ? `${dateRangeInfo.days}일간의 경기를 크롤링합니다`
                  : sport === 'all'
                    ? "선택한 날짜의 모든 종목 경기를 크롤링함"
                    : "선택한 종목의 모든 경기를 크롤링함"
            }
          >
            {loading ? '크롤링 중...' : (
              dateRangeInfo
                ? `${dateRangeInfo.days}일 크롤링`
                : (sport === 'all' ? '전체 크롤링' : '크롤링')
            )}
          </button>
          <button
            style={{...styles.button, ...styles.secondaryButton, alignSelf: 'flex-end'}}
            onClick={handlePartialQuery}
            disabled={loading}
            title={sport === 'all' ? "모든 종목의 1-2경기씩만 크롤링합니다 (테스트용)" : "선택한 종목의 1-2경기만 크롤링합니다 (테스트용)"}
          >
            {loading ? '크롤링 중...' : '1-2경기만'}
          </button>
        </div>
        {dateRangeInfo?.warning && (
          <div style={styles.warningText}>
            날짜 범위는 최대 31일까지만 가능함. (현재: {dateRangeInfo.days}일)
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    ...commonStyles.section,
    marginBottom: spacing[5],
  },
  title: {
    ...commonStyles.sectionTitle,
    margin: `0 0 ${spacing[5]} 0`,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[4],
  },
  formRow: {
    display: 'flex',
    gap: spacing[4],
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[1],
    minWidth: '100px',
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[800],
  },
  optionalText: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    fontWeight: typography.fontWeight.normal,
  },
  select: commonStyles.select,
  input: commonStyles.input,
  button: commonStyles.button,
  primaryButton: commonStyles.buttonPrimary,
  secondaryButton: commonStyles.buttonSecondary,
  warningText: {
    color: colors.status.error,
    fontSize: typography.fontSize.sm,
    marginTop: spacing[2],
  },
};

export default CrawlerRequest;
